import { useState, useEffect, useRef } from 'react';

import throttle from 'lodash/throttle';

import useExponentialBackoff from '../../../services/useExponentialBackoff';
import useInterval from '../../../services/useInterval';

export type TUseQuoteHealthCheckLoadingView = {
  isSubmitting: boolean;
  maxProgress: number;
  isComplete: boolean;
  completedOrders: string[];
  getQuote: () => void;
  onCompletion: () => void;
  numberOfChecklistItems?: number;
};

const INITIAL_GET_QUOTE_INTERVAL = 4000;
const PROGRESS_BAR_INCREMENT = 5;

export function useQuoteHealthCheckLoadingView({
  isSubmitting,
  maxProgress,
  isComplete,
  completedOrders,
  getQuote,
  onCompletion,
  numberOfChecklistItems,
}: TUseQuoteHealthCheckLoadingView) {
  const [isSubmittingOnMount, setIsSubmittingOnMount] = useState(isSubmitting);
  const [progressBarValue, setProgressBarValue] = useState(0);
  const [completedChecklistItemCount, setCompletedChecklistItemCount] =
    useState(0);
  const [getQuoteRequestCount, setGetQuoteRequestCount] = useState(0);
  const getQuoteInterval = useExponentialBackoff(
    getQuoteRequestCount,
    INITIAL_GET_QUOTE_INTERVAL
  );

  const maxProgressValue = isSubmittingOnMount ? maxProgress - 10 : maxProgress;

  const animationDuration = !isComplete
    ? 10000 * Math.pow(1 + progressBarValue / maxProgressValue, 2)
    : 2000;
  const intervalDelay =
    (animationDuration / maxProgressValue) * PROGRESS_BAR_INCREMENT;

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingOnMount(false);
    }
  }, [isSubmitting]);

  const completedOrdersLength = useRef(completedOrders.length);
  useEffect(
    function resetRequestCountAndIntervalForNewOrder() {
      if (completedOrders.length !== completedOrdersLength.current) {
        setGetQuoteRequestCount(0);
        completedOrdersLength.current = completedOrders.length;
      }
    },
    [completedOrders.length]
  );

  useInterval(function incrementProgressBar() {
    if (progressBarValue < maxProgressValue && !numberOfChecklistItems) {
      setProgressBarValue(progressBarValue + PROGRESS_BAR_INCREMENT);
    }
  }, intervalDelay);

  useInterval(function getQuoteAndIncrementCount() {
    if (!isSubmitting && !isComplete) {
      setGetQuoteRequestCount(
        (getQuoteRequestCount) => getQuoteRequestCount + 1
      );
      getQuote();
    }
  }, getQuoteInterval);

  useEffect(
    function delayHandleNext() {
      if (progressBarValue >= maxProgress && isComplete && !isSubmitting) {
        const successTimeout = setTimeout(() => onCompletion(), 500);
        return () => {
          clearTimeout(successTimeout);
        };
      }
    },
    [isComplete, isSubmitting, maxProgress, onCompletion, progressBarValue]
  );

  const throttledSetProgress = throttle(
    (newValue) => setProgressBarValue(newValue),
    500
  );

  function handleUpdate(itemProgress: number) {
    const progressPerChecklistItem = 100 / (numberOfChecklistItems || 1);
    const valueOfCompletedItems =
      progressPerChecklistItem * completedChecklistItemCount;
    const valueOfThisItem = (itemProgress / 100) * progressPerChecklistItem;
    const newValue = valueOfCompletedItems + valueOfThisItem;
    newValue !== progressBarValue && throttledSetProgress(newValue);
  }

  function handleCompletion() {
    setCompletedChecklistItemCount(completedChecklistItemCount + 1);
  }

  return {
    progressBarValue,
    completedChecklistItemCount,
    handleUpdate,
    handleCompletion,
  };
}
